import { allFunds, fundOf, holdingsOf, type Holding } from '~data';
import { classifiedSector } from '~/data/sectors';
import { classifiedCountry } from '~/data/countries';

/**
 * Walking a fund down to the things it actually owns.
 *
 * A fund of funds publishes ETFs where you want companies. HYLD's holdings page
 * is nine Hamilton tickers; its sector breakdown says 45.9% Information
 * Technology, and no row on either page is a technology company. The join
 * between them is a level down — inside SMAX and QMAX — and this is the walk
 * that gets there.
 *
 * Depth is not assumed. Today the deepest chain is fund → fund → company, but
 * HDIV holds CDAY which holds CMVP which holds companies, and nothing stops an
 * issuer adding another layer. The walk recurses until it runs out of funds,
 * with a visited set so a fund that reaches itself round a loop terminates, and
 * a depth ceiling as a backstop in case a future artefact does something the
 * visited set does not catch.
 *
 * Distinct from `bucketsFor` in exposure-store, which answers "what does this
 * fund resolve to on an axis" and prefers the issuer's own fund-level
 * breakdown. This answers "what does it end up holding", which is a different
 * question with a different shape — positions rather than buckets — and has no
 * published form to prefer.
 */

const MAX_DEPTH = 8;

const normalize = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Funds addressed by name, for the issuers that do not tag their own.
 *
 * Hamilton puts a ticker on every fund-of-funds holding. Harvest does not, so
 * HDIF's constituents arrive as "Harvest Tech Leaders Income ETF" and nothing
 * else — matched back by name, which is exact here because both strings are
 * written by the same issuer.
 */
const byName = new Map<string, string>();
for (const f of allFunds()) if (f.name != null) byName.set(normalize(f.name), f.symbol);

/** The artefact symbol for a holding that is itself a fund we carry, else null.
    This is also what makes a holding worth making clickable: it is exactly the
    set of rows there is somewhere to go to. */
export const fundSymbolFor = (holding: { symbol: string | null; name: string }): string | null => {
    if (holding.symbol != null) return fundOf(holding.symbol) == null ? null : holding.symbol;
    return byName.get(normalize(holding.name)) ?? null;
};

/** True where the walk can go a level deeper — a fund we carry *and* whose
    holdings we have. A fund we know only by name is a leaf, and saying so is
    better than dropping it. */
const canDescend = (holding: Holding): boolean => {
    const inner = fundSymbolFor(holding);
    return inner != null && holdingsOf(inner).length > 0;
};

export interface Leaf {
    /** Merge key — the ticker where there is one, else the name. */
    key: string;
    ticker: string | null;
    name: string;
    /** Weight in the fund the walk started from, multiplied down every level. */
    weight: number;
    /**
     * The funds that hold this position directly — the last hop, not the whole
     * chain.
     *
     * A route is not worth printing. Reaching RY inside HDIV runs
     * HDIV → HFIN → HFN → HMAX → CMVP, which tells a reader nothing they can
     * check, and a position reached by three routes has no single chain to
     * show. What is useful, and what Exposure's "held through" column already
     * says, is which fund to open to verify the number.
     */
    via: string[];
    /** As resolved against the fund that directly holds it — which is the only
        place it can be resolved, since both the issuer's tag and the app's
        classification are keyed by the holding fund. */
    sector: string | null;
    country: string | null;
}

const walk = (symbol: string, factor: number, depth: number, seen: ReadonlySet<string>, out: Leaf[]): void => {
    if (depth > MAX_DEPTH) return;

    for (const h of holdingsOf(symbol)) {
        const inner = fundSymbolFor(h);

        // Weights inside a fund are a percentage of that fund, so a level down
        // is worth its parent's weight times its own — 59.8% of HYLD in SMAX,
        // and 8% of SMAX in NVDA, is 4.8% of HYLD in NVDA. `factor` is already
        // that running product, so it carries down as the new weight rather
        // than being rescaled.
        const weight = (factor * h.weight) / 100;

        if (inner != null && !seen.has(inner) && holdingsOf(inner).length > 0) {
            walk(inner, weight, depth + 1, new Set([...seen, symbol]), out);
            continue;
        }

        out.push({
            key: h.symbol ?? `name:${normalize(h.name)}`,
            ticker: h.symbol,
            name: h.name,
            weight,
            via: [symbol],
            sector: h.sector ?? classifiedSector(symbol, h.symbol),
            country: h.country ?? classifiedCountry(symbol, h.symbol),
        });
    }
};

/**
 * Everything this fund ends up holding, merged.
 *
 * The same company reached through two funds is one position, not two — a
 * holder of HYLD owns one slice of NVDA, whichever wrappers it arrived through
 * — so duplicates are summed and both routes kept on `via`. That mirrors the
 * "held through" column on Exposure, which answers the same question one level
 * up, against the whole book rather than one fund.
 */
export const lookThrough = (symbol: string): Leaf[] => {
    const found: Leaf[] = [];
    walk(symbol, 100, 0, new Set(), found);

    const merged = new Map<string, Leaf>();
    for (const leaf of found) {
        const entry = merged.get(leaf.key);
        if (entry == null) {
            merged.set(leaf.key, { ...leaf, via: [...leaf.via] });
            continue;
        }
        entry.weight += leaf.weight;
        for (const hop of leaf.via) if (!entry.via.includes(hop)) entry.via.push(hop);
        // A sector resolved on one route and not the other is still resolved.
        entry.sector ??= leaf.sector;
        entry.country ??= leaf.country;
    }

    return [...merged.values()].sort((a, b) => b.weight - a.weight);
};

/** Whether the walk ever went deeper than the fund itself — which is what makes
    the route worth showing at all. A fund holding companies directly is its own
    look-through and needs no "via" column. */
export const isNested = (symbol: string): boolean => holdingsOf(symbol).some(canDescend);
